import { z } from 'zod';

const revision = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const proof = z
  .object({
    schema_version: z.literal(1),
    key_id: z.string().uuid(),
    challenge_id: z.string().uuid(),
    signature: z
      .string()
      .regex(/^[A-Za-z0-9_-]+$/)
      .max(128),
  })
  .strict();
const snapshotContext = {
  schema_version: z.literal(1),
  request_id: z.string().uuid(),
  installation_epoch: z.string().uuid().nullable(),
  auth_epoch: z.string().uuid(),
  state_version: revision,
};
const readyRegistration = z
  .object({
    ...snapshotContext,
    status: z.literal('READY'),
    installation_epoch: z.string().uuid(),
    operation_id: z.string().uuid(),
    binding_id: z.string().uuid(),
    expected_binding_revision: revision,
    identifier_kind: z.literal('fid'),
    fid: z
      .string()
      .min(1)
      .max(4096)
      .refine(fid => fid.trim().length > 0),
    sdk_registered: z.literal(true),
    refresh_mode: z.enum(['refreshing', 'non_refreshing']),
    installation_proof: proof,
  })
  .strict();
const registrationSnapshot = z.union([readyRegistration, z.object({ ...snapshotContext, status: z.enum(['PENDING', 'ROTATION_REQUIRED', 'UNAVAILABLE']) }).strict()]);
const registrationAck = z
  .object({
    schema_version: z.literal(1),
    state: z.literal('ACTIVE'),
    operation_id: z.string().uuid(),
    binding_id: z.string().uuid(),
    binding_revision: revision,
    registration_id: z.string().uuid(),
    registration_revision: revision.min(1),
    last_seen_at: z.string().datetime({ offset: true }),
    valid_until: z.string().datetime({ offset: true }),
    rotation_required: z.literal(false),
    relay_unbound: z.boolean(),
  })
  .strict();

type PushBridge = { postMessage(message: string): void };
type PushWindow = Window & { __safeonWorkerPush?: PushBridge };
type BridgeHandler = 'getWorkerPushRegistration' | 'ackWorkerPushRegistration' | 'workerPushSessionEnding';

class PushSyncFailure extends Error {
  constructor(readonly status?: number) {
    super('WORKER_PUSH_SYNC_UNAVAILABLE');
  }
}

function pushBridge(): PushBridge | undefined {
  return typeof window === 'undefined' || window !== window.top ? undefined : (window as PushWindow).__safeonWorkerPush;
}

/** The native listener supplies frame/origin metadata; legacy callHandler cannot replace it. */
export function callWorkerPushBridge(handler: BridgeHandler, request: Record<string, unknown> | null, signal: AbortSignal): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const bridge = pushBridge();
    if (!bridge || signal.aborted) return reject(new PushSyncFailure());
    const transportId = crypto.randomUUID();
    const finish = (result: unknown, failed = false) => {
      window.removeEventListener('safeonworkerpushreply', receive);
      signal.removeEventListener('abort', abort);
      clearTimeout(timeout);
      if (failed) reject(new PushSyncFailure());
      else resolve(result);
    };
    const receive = (event: Event) => {
      const detail: unknown = (event as CustomEvent<unknown>).detail;
      if (!detail || typeof detail !== 'object' || !('transport_id' in detail) || detail.transport_id !== transportId || !('result' in detail)) return;
      finish(detail.result);
    };
    const abort = () => finish(undefined, true);
    const timeout = setTimeout(abort, 12000);
    window.addEventListener('safeonworkerpushreply', receive);
    signal.addEventListener('abort', abort, { once: true });
    try {
      bridge.postMessage(JSON.stringify({ transport_id: transportId, handler, request }));
    } catch {
      finish(undefined, true);
    }
  });
}

/** This local stop signal never delays or replaces the authenticated server logout. */
export function signalWorkerPushSessionEnding(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('workerpushsessionending'));
  try {
    pushBridge()?.postMessage(JSON.stringify({ transport_id: crypto.randomUUID(), handler: 'workerPushSessionEnding', request: null }));
  } catch {
    // The app may already be detached while its web session is being cleared.
  }
}

/** One authenticated identity owns this lifetime, including every await and retry. */
export function startWorkerPushRegistrationSync(onAuthUnavailable: () => void, onRegistered?: () => void): () => void {
  let stopped = false;
  let generation = 0;
  let running = false;
  let requested = false;
  let retry = 0;
  let acknowledgedSnapshot: string | undefined;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let attempt: AbortController | undefined;
  const retryDelays = [1000, 4000, 15000];
  const current = (captured: number) => !stopped && captured === generation;

  const run = async () => {
    if (stopped || running || !requested || document.visibilityState === 'hidden') return;
    running = true;
    requested = false;
    const captured = generation;
    const controller = new AbortController();
    attempt = controller;
    const deadline = setTimeout(() => controller.abort(), 30000);
    try {
      const requestId = crypto.randomUUID();
      const raw = await callWorkerPushBridge('getWorkerPushRegistration', { schema_version: 1, request_id: requestId }, controller.signal);
      if (!current(captured)) return;
      const parsed = registrationSnapshot.safeParse(raw);
      if (!parsed.success || parsed.data.request_id !== requestId) throw new PushSyncFailure();
      const snapshot = parsed.data;
      if (snapshot.status !== 'READY') throw new PushSyncFailure();
      const snapshotKey = [snapshot.operation_id, snapshot.installation_epoch, snapshot.auth_epoch, snapshot.state_version].join(':');
      if (acknowledgedSnapshot === snapshotKey) return;
      const response = await fetch('/api/proxy/v1/worker-mobile/push/registration', {
        method: 'PUT',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        redirect: 'error',
        signal: controller.signal,
        body: JSON.stringify({
          schema_version: 1,
          operation_id: snapshot.operation_id,
          binding_id: snapshot.binding_id,
          expected_binding_revision: snapshot.expected_binding_revision,
          installation_epoch: snapshot.installation_epoch,
          identifier_kind: snapshot.identifier_kind,
          fid: snapshot.fid,
          refresh_mode: snapshot.refresh_mode,
          installation_proof: snapshot.installation_proof,
        }),
      });
      if (!current(captured)) return;
      if (!response.ok) throw new PushSyncFailure(response.status);
      const ack = registrationAck.safeParse(await response.json());
      if (!current(captured)) return;
      if (
        !ack.success ||
        ack.data.operation_id !== snapshot.operation_id ||
        ack.data.binding_id !== snapshot.binding_id ||
        ack.data.binding_revision !== snapshot.expected_binding_revision + 1 ||
        Date.parse(ack.data.valid_until) <= Date.now()
      ) {
        throw new PushSyncFailure();
      }
      const nativeAck = await callWorkerPushBridge(
        'ackWorkerPushRegistration',
        {
          schema_version: 1,
          operation_id: snapshot.operation_id,
          installation_epoch: snapshot.installation_epoch,
          auth_epoch: snapshot.auth_epoch,
          state_version: snapshot.state_version,
          binding_id: ack.data.binding_id,
          binding_revision: ack.data.binding_revision,
          registration_id: ack.data.registration_id,
          registration_revision: ack.data.registration_revision,
        },
        controller.signal
      );
      if (!nativeAck || typeof nativeAck !== 'object' || !('schema_version' in nativeAck) || nativeAck.schema_version !== 1 || 'error_code' in nativeAck) {
        throw new PushSyncFailure();
      }
      if (current(captured)) {
        acknowledgedSnapshot = snapshotKey;
        retry = 0;
        onRegistered?.();
      }
    } catch (error) {
      if (!current(captured)) return;
      if (error instanceof PushSyncFailure && error.status === 401) {
        onAuthUnavailable();
      } else if (retry < retryDelays.length) {
        timeout = setTimeout(() => {
          requested = true;
          void run();
        }, retryDelays[retry++]);
      }
    } finally {
      clearTimeout(deadline);
      running = false;
      if (attempt === controller) attempt = undefined;
      if (requested && !stopped) void run();
    }
  };
  const refresh = () => {
    if (stopped || document.visibilityState === 'hidden') return;
    generation += 1;
    retry = 0;
    clearTimeout(timeout);
    attempt?.abort();
    requested = true;
    void run();
  };
  const events = ['flutterInAppWebViewPlatformReady', 'safeonmobiledeviceready', 'workerpushregistrationchanged', 'pageshow', 'online'];
  const stop = () => {
    stopped = true;
    generation += 1;
    requested = false;
    clearTimeout(timeout);
    attempt?.abort();
    for (const event of events) window.removeEventListener(event, refresh);
    window.removeEventListener('workerpushsessionending', stop);
    document.removeEventListener('visibilitychange', refresh);
  };
  for (const event of events) window.addEventListener(event, refresh);
  window.addEventListener('workerpushsessionending', stop);
  document.addEventListener('visibilitychange', refresh);
  refresh();
  return stop;
}
